import clsx from "clsx";
import { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";

import { useCaptcha } from "@smart/bridge-captcha-dom";
import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { Button, FailureMessage } from "@smart/itops-sb-design-system-dom";

import { CaptchaChallenge } from "./captcha-challenge";
import { rootContainerId } from "./wrapper";
import { TeamItem, Toast } from "../../types";

export type SectionButtonsProps = {
  onPrevious?: (e: MouseEvent<HTMLButtonElement>) => void;
  onNext?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: (e: MouseEvent<HTMLButtonElement>) => void;
  setSidebar: (sidebar: boolean) => void;
  hasError?: string;
  hasPayment?: boolean;
  isForm: boolean;
  team: TeamItem;
  disabled?: {
    back?: boolean;
    next?: boolean;
    submit?: boolean;
  };
  children: ReactNode;
  showSectionsButton: boolean;
  isTeamHidden?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  isPrint?: boolean;
};

export const SectionButtons = ({
  onPrevious,
  onNext,
  onSubmit,
  setSidebar,
  hasError,
  hasPayment,
  team,
  isForm,
  disabled,
  children,
  showSectionsButton,
  isTeamHidden,
  setToasts,
  isPrint,
}: SectionButtonsProps) => {
  const { "LINK-2810": captchaEnabled } = useFeatureFlags();
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const captchaContext = useCaptcha();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      {
        root: document.getElementById(rootContainerId),
        /**
         * 70px is the height of the firm name header
         * 8px is the padding top of the sticky element
         * 1px is the triggering margin
         */
        rootMargin: isTeamHidden ? "-9px 0px 0px 0px" : "-79px 0px 0px 0px",
        threshold: 1,
      },
    );

    if (stickyRef.current) {
      observerRef.current.observe(stickyRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isTeamHidden]);

  if (isPrint) return children;

  const hasCaptchaToken =
    captchaContext && captchaEnabled ? captchaContext.token : true;

  return (
    <div className="flex flex-col">
      {hasError && (
        <FailureMessage
          className="mb-[1.6rem]"
          action="submit form"
          contactName={team.name || "us"}
          contactLink={(team.email && `mailto:${team.email}`) || undefined}
        />
      )}
      {/* This is a sticky detection element */}
      <div
        ref={stickyRef}
        className={clsx("sticky top-0", isSticky && "h-[5.6rem]")}
      />
      {(showSectionsButton || onNext || onPrevious) && (
        <div
          className={clsx(
            "mb-[1.6rem] bg-white z-1",
            isSticky &&
              "absolute left-0 right-0 shadow-modal @tablet:static @tablet:shadow-none",
            isSticky && !isTeamHidden && "top-[7rem]",
            isSticky && isTeamHidden && "top-[0rem]",
          )}
        >
          <div
            className={clsx(
              isSticky &&
                "mx-auto max-w-[90%] px-[3.2rem] py-[0.8rem] @tablet:px-0 @tablet:py-0 @tablet:max-w-full",
            )}
          >
            <div className="flex justify-between">
              {showSectionsButton && (
                <Button
                  className="@tablet:hidden"
                  variant="secondaryDefault"
                  text="Show Sections"
                  onClick={() => setSidebar(true)}
                />
              )}
              <div className="flex gap-medium">
                {onPrevious && (
                  <Button
                    variant="primarySubtle"
                    leftIcon="regularArrowLeft"
                    onClick={onPrevious}
                    disabled={disabled?.back}
                    dataTestId="previous-arrow-button"
                  />
                )}
                {onNext && (
                  <Button
                    variant="primarySubtle"
                    leftIcon="regularArrowRight"
                    type={isForm ? "submit" : "button"}
                    onClick={onNext}
                    disabled={disabled?.next}
                    dataTestId="next-arrow-button"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {children}
      <div className="flex flex-col gap-small">
        {captchaEnabled && onSubmit && (
          <CaptchaChallenge setToasts={setToasts} />
        )}
        <div className="flex flex-wrap gap-small">
          {onNext && (
            <Button
              text="Next"
              type={isForm ? "submit" : "button"}
              name="submit"
              value="next"
              onClick={onNext}
              variant="primaryDefault"
              disabled={disabled?.next}
            />
          )}
          {onSubmit && (
            <Button
              text={hasPayment ? "Submit and process payment" : "Submit"}
              variant="primaryDefault"
              type={isForm ? "submit" : "button"}
              name="submit"
              value="submit"
              onClick={onSubmit}
              disabled={disabled?.submit || !hasCaptchaToken}
            />
          )}
        </div>
      </div>
    </div>
  );
};
